import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import XLSX from 'xlsx-js-style'

import FilterSearch from 'pages/parts/FilterSearch'
import { GET, PGET } from 'helpers/api'
import { formatDate, formatDateBackend, formatMoney } from 'helpers/helpers'
import { styleExcelSheet } from 'helpers/excel'
import Pagination, { ItemPerPage } from "pages/parts/Pagination"

function Index() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const { productId } = useParams()

	const [data, setData] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [ItemsPerPage, setItemsPerPage] = useState(20)
	const [pageCount, setPageCount] = useState(0)
	const [filterData, setFilterData] = useState({
		startDate: null,
		endDate: null,
		search: '',
		page: 0,
	})
	const [URL, setURL] = useState(`/services/web/api/report-product-history/${productId}`)

	const productName = location.state?.productName

	const filteredRows = useMemo(() => {
		if (!filterData.search) return data

		return data.filter(item =>
			String(formatDate(item.actionDate) || '').toLowerCase().includes(filterData.search.toLowerCase())
		)
	}, [data, filterData.search])

	async function getData(filters = filterData, targetUrl = URL) {
		const params = {}
		if (filters.startDate) params.startDate = filters.startDate
		if (filters.endDate) params.endDate = filters.endDate
		params.page = filters.page || 0
		params.size = ItemsPerPage

		const response = await PGET(targetUrl, params, { loader: true })
		if (response) {
			setData(response.data)
			setPageCount(Math.ceil(response.headers['x-total-count'] / ItemsPerPage))
		}
	}

	function searchWithFilter() {
		const nextFilterData = { ...filterData, page: 0 }
		const nextUrl = `/services/web/api/report-product-history/${productId}`
		setFilterData(nextFilterData)
		setURL(nextUrl)
		setFilterDataExist(Boolean(nextFilterData.startDate || nextFilterData.endDate || nextFilterData.search))
		setDropdownIsOpen(false)
		getData(nextFilterData, nextUrl)
	}

	async function exportToExcel() {
		const params = {}
		if (filterData.startDate) params.startDate = filterData.startDate
		if (filterData.endDate) params.endDate = filterData.endDate

		const response = await GET(`/services/web/api/report-product-history-excel/${productId}`, params, { loader: true })
		if (!response) return

		const excelHeaders = [[
			t('DATE'),
			t('RECEIVED'),
			t('SOLD', 'Sold'),
			t('INVENTORY'),
			t('WRITE_OFF'),
			t('RETURN_TO_SUPPLIER'),
			t('RETURN', 'Client return'),
			t('TOTAL'),
		]]

		response.forEach(item => {
			excelHeaders.push([
				formatDate(item.actionDate),
				item.received,
				item.sold,
				item.inventory,
				item.writeOff,
				item.supplierReturn,
				item.clientReturn,
				item.total,
			])
		})

		const ws = XLSX.utils.aoa_to_sheet(excelHeaders)
		styleExcelSheet(ws)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, (productName || t('BALANCE_REPORT')) + ".xlsx")
	}

	function resetFilter() {
		const nextFilterData = {
			startDate: null,
			endDate: null,
			search: '',
			page: 0,
		}
		const nextUrl = `/services/web/api/report-product-history/${productId}`
		setFilterData(nextFilterData)
		setURL(nextUrl)
		setFilterDataExist(false)
		getData(nextFilterData, nextUrl)
	}

	async function paginate(data) {
		const nextFilterData = { ...filterData, page: data.selected }
		setFilterData(nextFilterData)
		getData(nextFilterData)
	}

	useEffect(() => {
		const nextUrl = `/services/web/api/report-product-history/${productId}`
		setURL(nextUrl)
		getData({ ...filterData, page: 0 }, nextUrl)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productId, ItemsPerPage])

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('BALANCE_REPORT')}
					{productName ? `: ${productName}` : ''}
				</h4>
				<button className="btn btn-outline-secondary" onClick={() => navigate('/report/balance')}>
					{t('BACK')}
				</button>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap">
							<div>
								<FilterSearch
									filterData={filterData}
									setFilterData={setFilterData}
								/>
							</div>

							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
										{t('FILTER')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('PERIOD')}</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.startDate ? new Date(filterData.startDate) : ''}
														onChange={(date) => setFilterData({ ...filterData, startDate: formatDateBackend(date) })}
													/>
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.endDate ? new Date(filterData.endDate) : ''}
														onChange={(date) => setFilterData({ ...filterData, endDate: formatDateBackend(date) })}
													/>
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end">
													<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
													<button className="btn btn-primary" onClick={searchWithFilter}>{t('APPLY')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>

							<div>
								<button className="btn btn-outline-primary btn-wide me-2" onClick={exportToExcel}>
									EXCEL
								</button>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('FILTER')}</strong>
								<br />
								{(filterData.startDate || filterData.endDate) &&
									<span className="me-2">
										<b>{t('PERIOD')}: </b>
										{filterData.startDate && <span>{formatDate(filterData.startDate)}</span>}
										{(filterData.startDate && filterData.endDate) && <span>|</span>}
										{filterData.endDate && <span>{formatDate(filterData.endDate)}</span>}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('SEARCH')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
							</div>
						</div>
					}

					<ItemPerPage
						itemsPerPage={ItemsPerPage}
						setItemsPerPage={(e) => {
							setItemsPerPage(e)
							setFilterData({ ...filterData, page: 0 })
						}}
					/>

					<div className="table-responsive">
						<table className="table table-hover cursor mb-0">
							<thead>
								<tr>
									<th>{t('DATE')}</th>
									<th className="text-end">{t('RECEIVED')}</th>
									<th className="text-end">{t('SOLD', 'Sold')}</th>
									<th className="text-end">{t('INVENTORY')}</th>
									<th className="text-end">{t('WRITE_OFF')}</th>
									<th className="text-end">{t('RETURN_TO_SUPPLIER')}</th>
									<th className="text-end">{t('RETURN', 'Client return')}</th>
									<th className="text-end">{t('TOTAL')}</th>
								</tr>
							</thead>
							<tbody>
								{filteredRows.map((item, index) => (
									<tr className="tr-middle" key={index}>
										<td>{formatDate(item.actionDate)}</td>
										<td className="text-end">{formatMoney(item.received)}</td>
										<td className="text-end">{formatMoney(item.sold)}</td>
										<td className="text-end">{formatMoney(item.inventory)}</td>
										<td className="text-end">{formatMoney(item.writeOff)}</td>
										<td className="text-end">{formatMoney(item.supplierReturn)}</td>
										<td className="text-end">{formatMoney(item.clientReturn)}</td>
										<td className="text-end">{formatMoney(item.total)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={filterData.page} />
				</div>
			</div>
		</>
	)
}

export default Index
